import type {
  AccountingAccount,
  AccountingAccountsUpdateRequest,
  AccountingRole,
} from '../../../types/models/AccountingAccount';

// The « Plan comptable » form: a draft per role, client-side validation mirroring the backend's rules
// (it re-validates and is the authority), and the PATCH body — only the roles and keys that changed, since
// the endpoint merges.

export interface AccountDraft {
  number: string;
  label: string;
  /** The WinAuditor supplier code — supplier roles (`F`) only, null for every other role. */
  thirdPartyCode: string | null;
}

export type AccountsDraft = Partial<Record<AccountingRole, AccountDraft>>;

const NUMBER_RE = /^[0-9]{3,10}$/;
const THIRD_PARTY_CODE_RE = /^[A-Za-z0-9]{1,10}$/;
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u001F\u007F]/;

/** Only a supplier role (WinAuditor `F`, i.e. `mollie_supplier`) has a third-party code. */
export const hasThirdPartyCode = (account: AccountingAccount): boolean => account.winauditor_type === 'F';

export function draftFrom(accounts: AccountingAccount[]): AccountsDraft {
  const draft: AccountsDraft = {};
  for (const a of accounts) {
    draft[a.role] = {
      number: a.number,
      label: a.label,
      thirdPartyCode: hasThirdPartyCode(a) ? (a.third_party_code ?? a.default_third_party_code ?? '') : null,
    };
  }
  return draft;
}

/** The draft of the role's defaults (« Valeur par défaut »). */
export function defaultDraft(account: AccountingAccount): AccountDraft {
  return {
    number: account.default_number,
    label: account.default_label,
    thirdPartyCode: hasThirdPartyCode(account) ? (account.default_third_party_code ?? '') : null,
  };
}

/** The French error of a PCMN number, or null when valid (3–10 digits). */
export function numberError(value: string): string | null {
  return NUMBER_RE.test(value.trim()) ? null : 'Le numéro doit compter de 3 à 10 chiffres.';
}

/** The French error of a label, or null when valid (trimmed, 1–80 characters, no control character). */
export function labelError(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length >= 1 && trimmed.length <= 80 && !CONTROL_RE.test(trimmed)
    ? null
    : "L'intitulé doit compter de 1 à 80 caractères, sans caractère de contrôle.";
}

/** The French error of a WinAuditor supplier code, or null when valid (trimmed, 1–10 digits / letters). */
export function thirdPartyCodeError(value: string): string | null {
  return THIRD_PARTY_CODE_RE.test(value.trim())
    ? null
    : 'Le code fournisseur doit compter de 1 à 10 chiffres ou lettres.';
}

/** Every draft row is valid. */
export function isDraftValid(draft: AccountsDraft): boolean {
  return Object.values(draft).every(
    (d) =>
      d !== undefined &&
      numberError(d.number) === null &&
      labelError(d.label) === null &&
      (d.thirdPartyCode === null || thirdPartyCodeError(d.thirdPartyCode) === null),
  );
}

/** The row equals the role's defaults. */
export function isAtDefault(account: AccountingAccount, row: AccountDraft | undefined): boolean {
  return (
    row !== undefined &&
    row.number.trim() === account.default_number &&
    row.label.trim() === account.default_label &&
    (row.thirdPartyCode === null || row.thirdPartyCode.trim() === (account.default_third_party_code ?? ''))
  );
}

/** The PATCH body with only the changed roles and keys, or null when nothing changed. */
export function buildAccountsPatch(
  accounts: AccountingAccount[],
  draft: AccountsDraft,
): AccountingAccountsUpdateRequest | null {
  const changes: AccountingAccountsUpdateRequest['accounts'] = {};
  for (const a of accounts) {
    const row = draft[a.role];
    if (row === undefined) {
      continue;
    }
    const change: { number?: string; label?: string; third_party_code?: string | null } = {};
    if (row.number.trim() !== a.number) {
      change.number = row.number.trim();
    }
    if (row.label.trim() !== a.label) {
      change.label = row.label.trim();
    }
    if (row.thirdPartyCode !== null) {
      const code = row.thirdPartyCode.trim();
      if (code !== (a.third_party_code ?? a.default_third_party_code ?? '')) {
        // Back to the default is `null`: sending the default's value would freeze it into the row.
        change.third_party_code = code === a.default_third_party_code ? null : code;
      }
    }
    if (Object.keys(change).length > 0) {
      changes[a.role] = change;
    }
  }
  return Object.keys(changes).length === 0 ? null : { accounts: changes };
}
